import { Link, useLocation } from "wouter";
import { 
  BarChart3, 
  Building2, 
  Users, 
  FileText, 
  Video, 
  Activity, 
  BellRing,
  PieChart,
  ClipboardCheck,
  ListChecks,
  ArrowLeft,
  Bot,
  Briefcase,
  Megaphone,
  FileSearch
} from "lucide-react";

export default function Layout({ children, cohortSlug }: { children: React.ReactNode; cohortSlug: string }) {
  const [location] = useLocation();
  const base = `/c/${cohortSlug}`;

  const navItems = [
    { href: base, label: "Command Center", icon: BarChart3 },
    { href: `${base}/intake`, label: "Company Intake", icon: Bot },
    { href: `${base}/companies`, label: "Companies", icon: Building2 },
    { href: `${base}/founders`, label: "Founders", icon: Users },
    { href: `${base}/investors`, label: "Investors", icon: PieChart },
    { href: "/admin/research-pipeline", label: "Research Pipeline", icon: FileSearch },
    { href: `${base}/review-queue`, label: "Review Queue", icon: ClipboardCheck },
    { href: `${base}/tasks`, label: "Tasks & Asks", icon: ListChecks },
    { href: `${base}/updates`, label: "Updates", icon: BellRing },
    { href: `${base}/documents`, label: "Vault", icon: FileText },
    { href: `${base}/media`, label: "Media", icon: Video },
    { href: `${base}/audit`, label: "Audit", icon: Activity },
    { href: `${base}/announcements`, label: "Announcements", icon: Megaphone },
    { href: `${base}/members`, label: "Members", icon: Users, separator: true },
    { href: "/admin/agents", label: "Agents", icon: Bot, separator: true },
    { href: "/admin/bankers", label: "Bankers", icon: Briefcase },
  ];

  return (
    <div className="flex min-h-screen w-full bg-background flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-sidebar border-r border-sidebar-border text-sidebar-foreground flex-shrink-0 flex flex-col hidden md:flex">
        <div className="p-6 border-b border-sidebar-border">
          <Link href="/admin">
            <div className="flex items-center gap-2 cursor-pointer group">
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">F</span>
              </div>
              <div>
                <h1 className="text-lg font-sans tracking-tight font-bold text-white">Favcy</h1>
                <p className="text-sidebar-foreground/50 text-[10px] font-mono uppercase tracking-[0.2em] leading-none">Favcy Select</p>
              </div>
            </div>
          </Link>
          <div className="mt-3 px-1">
            <Link href="/admin">
              <span className="text-[10px] text-sidebar-foreground/40 font-mono uppercase tracking-wider hover:text-sidebar-foreground/70 cursor-pointer flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> All Cohorts
              </span>
            </Link>
            <p className="text-xs text-primary font-medium mt-1 truncate">{cohortSlug.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item: any) => {
            const isActive = location === item.href || (item.href !== base && location.startsWith(item.href));
            return (
              <div key={item.href}>
                {item.separator && <div className="my-2 border-t border-sidebar-border" />}
                <Link 
                  href={item.href}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              </div>
            )
          })}
        </nav>
        <div className="p-4 border-t border-sidebar-border">
          <p className="text-[10px] text-sidebar-foreground/40 font-mono uppercase tracking-wider">Founder Intelligence OS</p>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 max-h-screen overflow-y-auto">
        <header className="md:hidden flex items-center justify-between p-4 border-b bg-background">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs">F</span>
            </div>
            <h1 className="text-lg font-sans font-bold">Favcy</h1>
          </div>
        </header>

        <div className="flex-1 p-6 md:p-10 max-w-7xl w-full mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
